import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import CatastroPage from "./pages/catastro/CatastroPage";
import DesarrolloUrbanoPage from "./pages/DesarrolloUrbanoPage";
import PublicidadPage from "./pages/publicidad/PublicidadPage";
import DefinicionesPage from "./pages/DefinicionesPage";
import ContribuyentesPage from "./pages/ContribuyentesPage";
import LAEPage from "./pages/LAEPage";
import CementerioPage from "./pages/CementerioPage";
import AseoUrbanoPage from "./pages/AseoUrbanoPage";
import LiquidacionesPage from "./pages/LiquidacionesPage";
import RecaudacionesPage from "./pages/recaudaciones/RecaudacionesPage";
import VehiculosPage from "./pages/vehiculos/VehiculosPage";
import AmbulantesPage from "./pages/ambulantes/AmbulantesPage";
import LicoresPage from "./pages/licores/LicoresPage";
import ReportesPage from "./pages/ReportesPage";
import ConfiguracionPage from "./pages/ConfiguracionPage";
import PerfilPage from "./pages/PerfilPage";
import SeguridadCuentaPage from "./pages/SeguridadCuentaPage";
import DashboardPage from "./pages/DashboardPage";
import MFAChallengePage from "./pages/MFAChallengePage";
import LandingPage from "./pages/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrimerAccesoPage from "./pages/PrimerAccesoPage";
import TerminosPage from "./pages/legales/TerminosPage";
import PrivacidadPage from "./pages/legales/PrivacidadPage";
import StatusPage from "./pages/legales/StatusPage";

// Portal del Contribuyente
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalComprobantesPage from "./pages/portal/PortalComprobantesPage";
import PortalPendientesPage from "./pages/portal/PortalPendientesPage";
import ComprobantesPago from "./pages/recaudaciones/ComprobantesPago";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// =============== Axios interceptor (auto-refresh on 401) ===============
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // SESSION-STORAGE (no localStorage): cada pestaña/ventana mantiene su sesión
  // aislada. Esto evita que un login en una pestaña sobrescriba la sesión de
  // otra pestaña abierta en el mismo navegador (p. ej. funcionario + contribuyente
  // simultáneamente). sessionStorage SOBREVIVE al F5 / refresh pero se borra al
  // cerrar la pestaña, que es el comportamiento esperado y seguro.
  const [token, setToken] = useState(sessionStorage.getItem("access_token"));
  const [refreshToken, setRefreshTokenState] = useState(sessionStorage.getItem("refresh_token"));
  const [loading, setLoading] = useState(true);

  // Axios global interceptor: auto-refresh si 401 por access_token expirado
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const isAuthEndpoint = original?.url?.includes("/auth/login") ||
                               original?.url?.includes("/auth/refresh") ||
                               original?.url?.includes("/auth/register");

        // Si es timeout por inactividad, NO intentar refresh — forzar logout limpio
        const detail = error.response?.data?.detail;
        const code = (detail && typeof detail === "object") ? detail.code : null;
        if (error.response?.status === 401 && code === "SESSION_IDLE_TIMEOUT") {
          sessionStorage.removeItem("access_token");
          sessionStorage.removeItem("refresh_token");
          setToken(null);
          setRefreshTokenState(null);
          setUser(null);
          toast.error(detail.message || "Tu sesión expiró por inactividad. Inicia sesión nuevamente.", { duration: 6000 });
          return Promise.reject(error);
        }

        if (
          error.response?.status === 401 &&
          !original._retry &&
          !isAuthEndpoint
        ) {
          const currentRefresh = sessionStorage.getItem("refresh_token");
          if (!currentRefresh) {
            return Promise.reject(error);
          }

          if (isRefreshing) {
            return new Promise((resolve) => {
              subscribeTokenRefresh((newToken) => {
                original.headers.Authorization = `Bearer ${newToken}`;
                resolve(axios(original));
              });
            });
          }

          original._retry = true;
          isRefreshing = true;
          try {
            const resp = await axios.post(`${API}/auth/refresh`, {
              refresh_token: currentRefresh,
            });
            const newAccess = resp.data.access_token;
            const newRefresh = resp.data.refresh_token;
            sessionStorage.setItem("access_token", newAccess);
            sessionStorage.setItem("refresh_token", newRefresh);
            setToken(newAccess);
            setRefreshTokenState(newRefresh);
            onRefreshed(newAccess);
            original.headers.Authorization = `Bearer ${newAccess}`;
            return axios(original);
          } catch (refreshErr) {
            sessionStorage.removeItem("access_token");
            sessionStorage.removeItem("refresh_token");
            setToken(null);
            setRefreshTokenState(null);
            setUser(null);
            toast.error("Sesión expirada. Inicie sesión nuevamente.");
            return Promise.reject(refreshErr);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          sessionStorage.removeItem("access_token");
          sessionStorage.removeItem("refresh_token");
          setToken(null);
          setRefreshTokenState(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const persistTokens = (access, refresh) => {
    if (access) {
      sessionStorage.setItem("access_token", access);
      setToken(access);
    }
    if (refresh) {
      sessionStorage.setItem("refresh_token", refresh);
      setRefreshTokenState(refresh);
    }
  };

  const login = async (email, password, altcha_payload = undefined, user_type = undefined) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password, altcha_payload, user_type });
      const data = response.data;

      if (data.mfa_required) {
        // Guarda mfa_token para la siguiente pantalla
        sessionStorage.setItem("mfa_token", data.mfa_token);
        return { success: true, mfaRequired: true };
      }

      persistTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      toast.success(`Bienvenido, ${data.user.nombre}`);
      if (data.mfa_suggested && !data.user.mfa_enabled) {
        setTimeout(() => {
          toast.info("Se recomienda activar autenticación en dos pasos (MFA) en su perfil.", { duration: 6000 });
        }, 1500);
      }
      return { success: true, mfaRequired: false };
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const completeMFA = async (code) => {
    const mfaToken = sessionStorage.getItem("mfa_token");
    if (!mfaToken) {
      toast.error("Sesión MFA expirada. Vuelva a iniciar sesión.");
      return { success: false };
    }
    try {
      const resp = await axios.post(`${API}/auth/mfa/challenge`, {
        mfa_token: mfaToken,
        code,
      });
      sessionStorage.removeItem("mfa_token");
      persistTokens(resp.data.access_token, resp.data.refresh_token);
      setUser(resp.data.user);
      toast.success(`Bienvenido, ${resp.data.user.nombre}`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Código inválido";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const data = response.data;
      persistTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      toast.success("Registro exitoso");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrar";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    const rt = sessionStorage.getItem("refresh_token");
    const at = sessionStorage.getItem("access_token");
    // Best-effort revoke server-side
    try {
      if (at) {
        await axios.post(
          `${API}/auth/logout`,
          { refresh_token: rt || undefined },
          { headers: { Authorization: `Bearer ${at}` } }
        );
      }
    } catch (_) {
      // ignore; limpiamos local igualmente
    }
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("mfa_token");
    // Limpiar también restos legacy en localStorage (de versiones anteriores)
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setToken(null);
    setRefreshTokenState(null);
    setUser(null);
    toast.info("Sesión cerrada");
  };

  const fetchUser = async () => {
    const at = sessionStorage.getItem("access_token");
    if (!at) return null;
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${at}` } });
      setUser(r.data);
      setToken(at);
      return r.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, refreshToken, loading,
      login, register, logout, completeMFA, fetchUser, API,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Forzar primer acceso obligatorio si el user requiere setup (cuenta creada por funcionario)
  const requiresFirstTimeSetup = user.must_change_password || user.must_accept_terms;
  if (requiresFirstTimeSetup && location.pathname !== "/portal/primer-acceso") {
    return <Navigate to="/portal/primer-acceso" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{ style: { fontFamily: 'Inter, sans-serif' } }}
        />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/mfa-challenge" element={<MFAChallengePage />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/portal/primer-acceso" element={
            <ProtectedRoute>
              <PrimerAccesoPage />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="catastro" element={<CatastroPage />} />
            <Route path="catastro/:submodulo" element={<CatastroPage />} />
            <Route path="desarrollo-urbano" element={<DesarrolloUrbanoPage />} />
            <Route path="desarrollo-urbano/:submodulo" element={<DesarrolloUrbanoPage />} />
            <Route path="publicidad" element={<PublicidadPage />} />
            <Route path="publicidad/:submodulo" element={<PublicidadPage />} />
            <Route path="vehiculos/:submodulo" element={<VehiculosPage />} />
            <Route path="ambulantes" element={<AmbulantesPage />} />
            <Route path="ambulantes/:submodulo" element={<AmbulantesPage />} />
            <Route path="licores" element={<LicoresPage />} />
            <Route path="licores/:submodulo" element={<LicoresPage />} />
            <Route path="definiciones" element={<DefinicionesPage />} />
            <Route path="definiciones/:submodulo" element={<DefinicionesPage />} />
            <Route path="contribuyentes" element={<ContribuyentesPage />} />
            <Route path="contribuyentes/:submodulo" element={<ContribuyentesPage />} />
            <Route path="lae" element={<LAEPage />} />
            <Route path="cementerio" element={<CementerioPage />} />
            <Route path="aseo-urbano" element={<AseoUrbanoPage />} />
            <Route path="liquidaciones" element={<LiquidacionesPage />} />
            <Route path="liquidaciones/:submodulo" element={<LiquidacionesPage />} />
            <Route path="recaudaciones" element={<RecaudacionesPage />} />
            <Route path="recaudaciones/:submodulo" element={<RecaudacionesPage />} />
            <Route path="reportes" element={<ReportesPage />} />
            <Route path="reportes/:subreporte" element={<ReportesPage />} />
            <Route path="configuracion" element={<ConfiguracionPage />} />
            <Route path="perfil" element={<PerfilPage />} />
            <Route path="perfil/seguridad" element={<SeguridadCuentaPage />} />
            <Route path="portal/dashboard" element={<PortalDashboard />} />
            <Route path="portal/declaracion" element={<LiquidacionesPage portalMode />} />
            <Route path="portal/reporte-pago" element={<ComprobantesPago portalMode />} />
            <Route path="portal/comprobantes" element={<PortalComprobantesPage />} />
            <Route path="portal/pendientes" element={<PortalPendientesPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
